// frontend/src/modules/bancario/pages/DashboardBancario.js
import React, { useState, useEffect } from 'react';
import { FaMoneyBill, FaCreditCard, FaQrcode, FaBarcode, FaArrowUp, FaArrowDown, FaSync } from 'react-icons/fa';
import { useMediaQuery } from 'react-responsive';

function DashboardBancario({ usuario }) {
  const [saldo, setSaldo] = useState(null);
  const [extrato, setExtrato] = useState([]);
  const [pixRecebidos, setPixRecebidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [periodo, setPeriodo] = useState({
    dataInicio: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    dataFim: new Date().toISOString().split('T')[0]
  });

  const isMobile = useMediaQuery({ maxWidth: 768 });
  const token = localStorage.getItem('token');
  const empresaId = usuario?.empresa_id || 1;

  useEffect(() => {
    fetchDados();
  }, [periodo]);

  const fetchDados = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/lancamentos/empresa/${empresaId}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erro ${response.status} ao buscar dados`);
      }

      const lancamentos = await response.json();
      
      const lancamentosFiltrados = lancamentos.filter(item => {
        const data = new Date(item.data_lancamento);
        const dataInicio = new Date(periodo.dataInicio);
        const dataFim = new Date(periodo.dataFim);
        dataFim.setHours(23, 59, 59);
        return data >= dataInicio && data <= dataFim;
      });

      let saldoTotal = 0;
      lancamentosFiltrados.forEach(item => {
        const valor = Number(item.valor) || 0;
        if (item.tipo === 'RECEITA') {
          saldoTotal += valor;
        } else if (item.tipo === 'DESPESA') {
          saldoTotal -= valor;
        }
      });

      setSaldo({
        saldo: saldoTotal,
        limite: 10000
      });

      const pix = lancamentosFiltrados
        .filter(item => {
          const descricao = (item.descricao || '').toLowerCase();
          return descricao.includes('pix') || item.forma_pagamento === 'PIX';
        })
        .slice(0, 10);
      setPixRecebidos(pix);

      setExtrato(lancamentosFiltrados.slice(0, 20));
      
    } catch (err) {
      console.error('Erro detalhado:', err);
      setError(err.message);
      setSaldo({ saldo: 15000, limite: 10000 });
      setPixRecebidos([]);
      setExtrato([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '50px' }}>🔄 Carregando dados bancários...</div>;
  
  if (error) return (
    <div style={{ textAlign: 'center', padding: '50px' }}>
      <div style={{ fontSize: '48px', marginBottom: '10px' }}>❌</div>
      <p style={{ color: 'red', fontSize: isMobile ? '14px' : '16px' }}>{error}</p>
      <button 
        onClick={fetchDados}
        style={{ 
          marginTop: '10px', 
          padding: isMobile ? '8px 20px' : '10px 20px', 
          backgroundColor: '#007bff', 
          color: 'white', 
          border: 'none', 
          borderRadius: '4px', 
          cursor: 'pointer',
          fontSize: isMobile ? '13px' : '14px',
          width: isMobile ? '100%' : 'auto'
        }}
      >
        <FaSync /> Tentar Novamente
      </button>
    </div>
  );

  return (
    <div style={{ padding: isMobile ? '10px' : '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Cabeçalho */}
      <div style={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'stretch' : 'center', 
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? '20px' : '28px' }}>🏦 Dashboard Bancário</h2>
          <p style={{ color: '#6c757d', margin: '5px 0 0 0', fontSize: isMobile ? '13px' : '14px' }}>
            Acompanhe suas movimentações financeiras
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <input 
            type="date" 
            value={periodo.dataInicio} 
            onChange={(e) => setPeriodo({...periodo, dataInicio: e.target.value})}
            style={{ 
              padding: isMobile ? '6px 10px' : '8px 12px', 
              borderRadius: '4px', 
              border: '1px solid #ddd',
              fontSize: isMobile ? '12px' : '14px',
              flex: isMobile ? '1' : '0'
            }}
          />
          <input 
            type="date" 
            value={periodo.dataFim} 
            onChange={(e) => setPeriodo({...periodo, dataFim: e.target.value})}
            style={{ 
              padding: isMobile ? '6px 10px' : '8px 12px', 
              borderRadius: '4px', 
              border: '1px solid #ddd',
              fontSize: isMobile ? '12px' : '14px',
              flex: isMobile ? '1' : '0'
            }}
          />
          <button 
            onClick={fetchDados}
            style={{ 
              padding: isMobile ? '6px 12px' : '8px 16px', 
              backgroundColor: '#007bff', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer',
              fontSize: isMobile ? '12px' : '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              flex: isMobile ? '1' : '0',
              justifyContent: 'center'
            }}
          >
            <FaSync /> Atualizar
          </button>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', 
        gap: isMobile ? '10px' : '20px', 
        marginBottom: '30px' 
      }}>
        <div style={{ backgroundColor: '#cce5ff', padding: isMobile ? '15px' : '20px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: 0, color: '#004085', fontSize: isMobile ? '14px' : '18px' }}><FaMoneyBill /> Saldo</h3>
          <p style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 'bold', margin: '10px 0 0 0', color: '#004085' }}>
            {formatCurrency(saldo?.saldo || 0)}
          </p>
          <small style={{ color: '#6c757d', fontSize: isMobile ? '11px' : '12px' }}>Limite: {formatCurrency(saldo?.limite || 0)}</small>
        </div>
        <div style={{ backgroundColor: '#d4edda', padding: isMobile ? '15px' : '20px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: 0, color: '#155724', fontSize: isMobile ? '14px' : '18px' }}><FaQrcode /> PIX Recebidos</h3>
          <p style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 'bold', margin: '10px 0 0 0', color: '#155724' }}>
            {pixRecebidos.length}
          </p>
        </div>
        <div style={{ backgroundColor: '#fff3cd', padding: isMobile ? '15px' : '20px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: 0, color: '#856404', fontSize: isMobile ? '14px' : '18px' }}><FaBarcode /> Movimentações</h3>
          <p style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 'bold', margin: '10px 0 0 0', color: '#856404' }}>
            {extrato.length}
          </p>
        </div>
      </div>

      {/* Últimos PIX Recebidos */}
      <div style={{ backgroundColor: 'white', padding: isMobile ? '10px' : '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '20px', overflowX: 'auto' }}>
        <h3 style={{ fontSize: isMobile ? '16px' : '18px' }}>📋 Últimos PIX Recebidos</h3>
        {pixRecebidos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p style={{ color: '#6c757d', fontSize: isMobile ? '13px' : '14px' }}>📭 Nenhum PIX recebido no período</p>
          </div>
        ) : isMobile ? (
          // Mobile - Cards
          <div>
            {pixRecebidos.map((pix, index) => (
              <div key={index} style={{
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                padding: '10px',
                marginBottom: '8px',
                borderLeft: '3px solid #28a745'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', fontWeight: 'bold' }}>{pix.pessoa_nome || pix.infoPagador || 'Cliente'}</span>
                  <span style={{ fontWeight: 'bold', color: '#28a745', fontSize: '14px' }}>
                    {formatCurrency(pix.valor)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#6c757d', marginTop: '4px' }}>
                  <span>📅 {formatDate(pix.data_lancamento || pix.data)}</span>
                  <span style={{ padding: '2px 8px', borderRadius: '10px', backgroundColor: '#d4edda', color: '#155724', fontSize: '10px' }}>
                    ✅ CONCLUÍDO
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Desktop - Tabela
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Data</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Pagador</th>
                <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #dee2e6' }}>Valor</th>
                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {pixRecebidos.map((pix, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '10px' }}>{formatDate(pix.data_lancamento || pix.data)}</td>
                  <td style={{ padding: '10px' }}>{pix.pessoa_nome || pix.infoPagador || 'Cliente'}</td>
                  <td style={{ padding: '10px', textAlign: 'right', color: '#28a745', fontWeight: 'bold' }}>
                    {formatCurrency(pix.valor)}
                  </td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    <span style={{ padding: '3px 10px', borderRadius: '12px', backgroundColor: '#d4edda', color: '#155724' }}>
                      ✅ CONCLUÍDO
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Extrato Bancário */}
      <div style={{ backgroundColor: 'white', padding: isMobile ? '10px' : '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
        <h3 style={{ fontSize: isMobile ? '16px' : '18px' }}>📋 Extrato Bancário</h3>
        {extrato.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p style={{ color: '#6c757d', fontSize: isMobile ? '13px' : '14px' }}>📭 Nenhuma movimentação no período</p>
          </div>
        ) : isMobile ? (
          // Mobile - Cards
          <div>
            {extrato.map((item, index) => (
              <div key={index} style={{
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                padding: '10px',
                marginBottom: '8px',
                borderLeft: `3px solid ${item.tipo === 'RECEITA' ? '#28a745' : '#dc3545'}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', fontWeight: 'bold' }}>{item.descricao}</span>
                  <span style={{ fontWeight: 'bold', color: item.tipo === 'RECEITA' ? '#28a745' : '#dc3545', fontSize: '14px' }}>
                    {formatCurrency(item.valor)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#6c757d', marginTop: '4px' }}>
                  <span>📅 {formatDate(item.data_lancamento || item.data)}</span>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '10px',
                    backgroundColor: item.tipo === 'RECEITA' ? '#d4edda' : '#f8d7da',
                    color: item.tipo === 'RECEITA' ? '#155724' : '#721c24',
                    fontSize: '10px'
                  }}>
                    {item.tipo === 'RECEITA' ? '💰 Crédito' : '💸 Débito'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Desktop - Tabela
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Data</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Descrição</th>
                <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #dee2e6' }}>Valor</th>
                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Tipo</th>
              </tr>
            </thead>
            <tbody>
              {extrato.map((item, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '10px' }}>{formatDate(item.data_lancamento || item.data)}</td>
                  <td style={{ padding: '10px' }}>{item.descricao}</td>
                  <td style={{ padding: '10px', textAlign: 'right', color: item.tipo === 'RECEITA' ? '#28a745' : '#dc3545', fontWeight: 'bold' }}>
                    {formatCurrency(item.valor)}
                  </td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    <span style={{
                      padding: '3px 10px',
                      borderRadius: '12px',
                      backgroundColor: item.tipo === 'RECEITA' ? '#d4edda' : '#f8d7da',
                      color: item.tipo === 'RECEITA' ? '#155724' : '#721c24'
                    }}>
                      {item.tipo === 'RECEITA' ? '💰 Crédito' : '💸 Débito'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default DashboardBancario;